import React from 'react';
import { defaultMemoize } from 'reselect';
import { Messages } from '../../../../../gen-src/Localize';
import { DatabaseQuery } from '../../../../../gen-src/queries';
import { SelectionProviderChildProps } from '../../../data-catalog-shared';
import { LineageItemType } from '../lineage/LineageItemType';
import { LineageItemConfig, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { PaneWithTitle } from '../placePage/PaneWithTitle';
import { DashboardsGridSortProvider } from '../sorting/sortProviders/DashboardsGridSortProvider';
import { DatabasesGridSortProvider } from '../sorting/sortProviders/DatabasesGridSortProvider';
import { DatasourcesGridSortProvider } from '../sorting/sortProviders/DatasourcesGridSortProvider';
import { FlowsGridSortProvider } from '../sorting/sortProviders/FlowsGridSortProvider';
import { LensesGridSortProvider } from '../sorting/sortProviders/LensesGridSortProvider';
import { MetricsGridSortProvider } from '../sorting/sortProviders/MetricsGridSortProvider';
import { SheetsGridSortProvider } from '../sorting/sortProviders/SheetsGridSortProvider';
import { TablesGridSortProvider } from '../sorting/sortProviders/TablesGridSortProvider';
import { VirtualConnectionTablesGridSortProvider } from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import { VirtualConnectionsGridSortProvider } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';
import { WorkbooksGridSortProvider } from '../sorting/sortProviders/WorkbooksGridSortProvider';
import { DatabaseDownstreamDashboardsQueryGrid } from './DatabaseDownstreamDashboardsQueryGrid';
import { DatabaseDownstreamDatabasesQueryGrid } from './DatabaseDownstreamDatabasesQueryGrid';
import { DatabaseDownstreamDatasourcesQueryGrid } from './DatabaseDownstreamDatasourcesQueryGrid';
import { DatabaseDownstreamFlowsQueryGrid } from './DatabaseDownstreamFlowsQueryGrid';
import { DatabaseDownstreamLensesQueryGrid } from './DatabaseDownstreamLensesQueryGrid';
import { DatabaseDownstreamMetricsQueryGrid } from './DatabaseDownstreamMetricsQueryGrid';
import { DatabaseDownstreamOwnersPane } from './DatabaseDownstreamOwnersPane';
import { DatabaseDownstreamSheetsQueryGrid } from './DatabaseDownstreamSheetsQueryGrid';
import { DatabaseDownstreamTablesQueryGrid } from './DatabaseDownstreamTablesQueryGrid';
import { DatabaseDownstreamVirtualConnectionTablesQueryGrid } from './DatabaseDownstreamVirtualConnectionTablesQueryGrid';
import { DatabaseDownstreamVirtualConnectionsQueryGrid } from './DatabaseDownstreamVirtualConnectionsQueryGrid';
import { DatabaseDownstreamWorkbooksQueryGrid } from './DatabaseDownstreamWorkbooksQueryGrid';
import { DatabaseLineageCounts } from './DatabaseLineageCountsQueryWrapper';
import { DatabaseTablesPane } from './DatabaseTablesPane';
import { DatabaseUpstreamDatabasesQueryGrid } from './DatabaseUpstreamDatabasesQueryGrid';
import { DatabaseUpstreamDatasourcesQueryGrid } from './DatabaseUpstreamDatasourcesQueryGrid';
import { DatabaseUpstreamFlowsQueryGrid } from './DatabaseUpstreamFlowsQueryGrid';
import { DatabaseUpstreamTablesQueryGrid } from './DatabaseUpstreamTablesQueryGrid';

export type DatabaseLineageItemConfig = LineageItemConfig<DatabaseLineageCounts, DatabaseLineagePaneParams>;
export type DatabaseLineageSection = LineageSection<DatabaseLineageCounts, DatabaseLineagePaneParams>;
type Database = Unpacked<DatabaseQuery['databases']>

export type DatabaseLineagePaneParams = SelectionProviderChildProps & {
  readonly title: string;
  readonly database: Database;
  readonly databaseId: string;
  readonly filteredTableId: string | null;
  readonly count: number | null;
};

const DatabaseUpstreamItems: ReadonlyArray<DatabaseLineageItemConfig> = [
  {
    lineageItemType: LineageItemType.Database,
    messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
    messagesForPane: { withCount: Messages.UpstreamDatabasesWithCount, withoutCount: Messages.UpstreamDatabases },
    count: counts => counts.upstreamDatabases,
    path: (urls, id) => urls.catalogDatabaseUpstreamDatabasesPath(id),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <DatabasesGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <DatabaseUpstreamDatabasesQueryGrid
              databaseId={paneParams.databaseId}
              filteredTableId={paneParams.filteredTableId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />
          }
        </DatabasesGridSortProvider>
      </PaneWithTitle>
    ),
  },
  {
    lineageItemType: LineageItemType.Table,
    messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
    messagesForPane: { withCount: Messages.UpstreamTablesWithCount, withoutCount: Messages.UpstreamTables },
    count: counts => counts.upstreamTables,
    path: (urls, id) => urls.catalogDatabaseUpstreamTablesPath(id),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <TablesGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <DatabaseUpstreamTablesQueryGrid
              databaseId={paneParams.databaseId}
              filteredTableId={paneParams.filteredTableId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />
          }
        </TablesGridSortProvider>
      </PaneWithTitle>
    ),
  },
  {
    lineageItemType: LineageItemType.Flow,
    messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
    messagesForPane: { withCount: Messages.UpstreamFlowsWithCount, withoutCount: Messages.UpstreamFlows },
    count: counts => counts.upstreamFlows,
    path: (urls, id) => urls.catalogDatabaseUpstreamFlowsPath(id),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <FlowsGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <DatabaseUpstreamFlowsQueryGrid
              databaseId={paneParams.databaseId}
              filteredTableId={paneParams.filteredTableId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />
          }
        </FlowsGridSortProvider>
      </PaneWithTitle>
    ),
  },
  {
    lineageItemType: LineageItemType.Datasource,
    messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
    messagesForPane: { withCount: Messages.UpstreamDatasourcesWithCount, withoutCount: Messages.UpstreamDatasources },
    count: counts => counts.upstreamDatasources,
    path: (urls, id) => urls.catalogDatabaseUpstreamDatasourcesPath(id),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <DatasourcesGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <DatabaseUpstreamDatasourcesQueryGrid
              databaseId={paneParams.databaseId}
              filteredTableId={paneParams.filteredTableId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />}
        </DatasourcesGridSortProvider>
      </PaneWithTitle>
    ),
  },
];

const DatabaseAnchor: DatabaseLineageItemConfig = {
  lineageItemType: LineageItemType.Database,
  messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
  messagesForPane: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
  count: counts => counts.tables,
  path: (urls, databaseId) => urls.catalogDatabaseTablesPath(databaseId),
  paneFactory: (paneParams) => (
    <DatabaseTablesPane
      title={paneParams.title}
      database={paneParams.database}
      databaseId={paneParams.databaseId}
      selectedKeys={paneParams.selectedKeys}
      updateSelectionForKey={paneParams.updateSelectionForKey}
      rowCount={paneParams.count}
    />
  )
};

const databaseDownstreamMetrics: DatabaseLineageItemConfig = {
  lineageItemType: LineageItemType.Metric,
  messagesForLineage: { withCount: Messages.MetricsWithCount, withoutCount: Messages.Metrics },
  messagesForPane: { withCount: Messages.DownstreamMetricsWithCount, withoutCount: Messages.DownstreamMetrics },
  count: counts => counts.downstreamMetrics,
  path: (urls, databaseId) => urls.catalogDatabaseDownstreamMetricsPath(databaseId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <MetricsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <DatabaseDownstreamMetricsQueryGrid
            databaseId={paneParams.databaseId}
            filteredTableId={paneParams.filteredTableId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />}
      </MetricsGridSortProvider>
    </PaneWithTitle>
  )
};

const databaseDownstreamVirtualConnection: DatabaseLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnection,
  messagesForLineage: { withCount: Messages.VirtualConnectionsWithCount, withoutCount: Messages.VirtualConnections },
  messagesForPane: { withCount: Messages.DownstreamVirtualConnectionsWithCount, withoutCount: Messages.DownstreamVirtualConnections },
  count: counts => counts.downstreamVirtualConnections,
  path: (urls, vizportalUrlId) => urls.catalogDatabaseDownstreamVirtualConnectionsPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <VirtualConnectionsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <DatabaseDownstreamVirtualConnectionsQueryGrid
            databaseId={paneParams.databaseId}
            filteredTableId={paneParams.filteredTableId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </VirtualConnectionsGridSortProvider>
    </PaneWithTitle>
  ),
};

const databaseDownstreamVirtualConnectionTable: DatabaseLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnectionTable,
  messagesForLineage: { withCount: Messages.VirtualConnectionTablesWithCount, withoutCount: Messages.VirtualConnectionTables },
  messagesForPane: { withCount: Messages.DownstreamVirtualConnectionTablesWithCount, withoutCount: Messages.DownstreamVirtualConnectionTables },
  count: counts => counts.downstreamVirtualConnectionTables,
  path: (urls, vizportalUrlId) => urls.catalogDatabaseDownstreamVirtualConnectionTablesPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <VirtualConnectionTablesGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <DatabaseDownstreamVirtualConnectionTablesQueryGrid
            databaseId={paneParams.databaseId}
            filteredTableId={paneParams.filteredTableId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </VirtualConnectionTablesGridSortProvider>
    </PaneWithTitle>
  ),
};

const databaseDownstreamLenses: DatabaseLineageItemConfig = {
  lineageItemType: LineageItemType.Lens,
  messagesForLineage: { withCount: Messages.LensesWithCount, withoutCount: Messages.Lenses },
  messagesForPane: { withCount: Messages.DownstreamLensesWithCount, withoutCount: Messages.DownstreamLenses },
  count: counts => counts.downstreamLenses,
  path: (urls, vizportalUrlId) => urls.catalogDatabaseDownstreamLensesPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <LensesGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <DatabaseDownstreamLensesQueryGrid
            databaseId={paneParams.databaseId}
            filteredTableId={paneParams.filteredTableId}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />}
      </LensesGridSortProvider>
    </PaneWithTitle>
  ),
};


function getDatabaseDownstreamItems(
  metricsDataCatalogEnabled: boolean,
  publishedConnectionsLineageEnabled: boolean,
  lensLineageEnabled: boolean,
): ReadonlyArray<DatabaseLineageItemConfig> {
  return [
    {
      lineageItemType: LineageItemType.Database,
      messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
      messagesForPane: { withCount: Messages.DownstreamDatabasesWithCount, withoutCount: Messages.DownstreamDatabases },
      count: counts => counts.downstreamDatabases,
      path: (urls, databaseId) => urls.catalogDatabaseDownstreamDatabasesPath(databaseId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatabasesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatabaseDownstreamDatabasesQueryGrid
                databaseId={paneParams.databaseId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </DatabasesGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Table,
      messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
      messagesForPane: { withCount: Messages.DownstreamTablesWithCount, withoutCount: Messages.DownstreamTables },
      count: counts => counts.downstreamTables,
      path: (urls, databaseId) => urls.catalogDatabaseDownstreamTablesPath(databaseId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <TablesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatabaseDownstreamTablesQueryGrid
                databaseId={paneParams.databaseId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </TablesGridSortProvider>
        </PaneWithTitle>
      )
    },
    ...(publishedConnectionsLineageEnabled ? [databaseDownstreamVirtualConnection, databaseDownstreamVirtualConnectionTable] : []),
    {
      lineageItemType: LineageItemType.Flow,
      messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
      messagesForPane: { withCount: Messages.DownstreamFlowsWithCount, withoutCount: Messages.DownstreamFlows },
      count: counts => counts.downstreamFlows,
      path: (urls, databaseId) => urls.catalogDatabaseDownstreamFlowsPath(databaseId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <FlowsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatabaseDownstreamFlowsQueryGrid
                databaseId={paneParams.databaseId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FlowsGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Datasource,
      messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
      messagesForPane: { withCount: Messages.DownstreamDatasourcesWithCount, withoutCount: Messages.DownstreamDatasources },
      count: counts => counts.downstreamDatasources,
      path: (urls, databaseId) => urls.catalogDatabaseDownstreamDatasourcesPath(databaseId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatasourcesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatabaseDownstreamDatasourcesQueryGrid
                databaseId={paneParams.databaseId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DatasourcesGridSortProvider>
        </PaneWithTitle>
      )
    },
    ...(lensLineageEnabled ? [databaseDownstreamLenses] : []),
    {
      lineageItemType: LineageItemType.Workbook,
      messagesForLineage: { withCount: Messages.WorkbooksWithCount, withoutCount: Messages.Workbooks },
      messagesForPane: { withCount: Messages.DownstreamWorkbooksWithCount, withoutCount: Messages.DownstreamWorkbooks },
      count: counts => counts.downstreamWorkbooks,
      path: (urls, databaseId) => urls.catalogDatabaseDownstreamWorkbooksPath(databaseId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <WorkbooksGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatabaseDownstreamWorkbooksQueryGrid
                databaseId={paneParams.databaseId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </WorkbooksGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Sheet,
      messagesForLineage: { withCount: Messages.SheetsWithCount, withoutCount: Messages.Sheets },
      messagesForPane: { withCount: Messages.DownstreamSheetsWithCount, withoutCount: Messages.DownstreamSheets },
      count: counts => counts.downstreamSheets,
      path: (urls, databaseId) => urls.catalogDatabaseDownstreamSheetsPath(databaseId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <SheetsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatabaseDownstreamSheetsQueryGrid
                databaseId={paneParams.databaseId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </SheetsGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Dashboard,
      messagesForLineage: { withCount: Messages.DashboardsWithCount, withoutCount: Messages.Dashboards },
      messagesForPane: { withCount: Messages.DownstreamDashboardsWithCount, withoutCount: Messages.DownstreamDashboards },
      count: counts => counts.downstreamDashboards,
      path: (urls, vizportalUrlId) => urls.catalogDatabaseDownstreamDashboardsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DashboardsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <DatabaseDownstreamDashboardsQueryGrid
                databaseId={paneParams.databaseId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DashboardsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    ...(metricsDataCatalogEnabled ? [databaseDownstreamMetrics] : []),
  ];
}
const memoizedGetDatabaseDownstreamItems = defaultMemoize(getDatabaseDownstreamItems);

const DatabaseOwners: DatabaseLineageItemConfig = {
  lineageItemType: LineageItemType.User,
  messagesForLineage: { withCount: Messages.OwnersWithCount, withoutCount: Messages.Owners },
  messagesForPane: { withCount: Messages.DownstreamOwnersWithCount, withoutCount: Messages.DownstreamOwners },
  count: counts => counts.downstreamOwners,
  path: (urls, databaseId) => urls.catalogDatabaseDownstreamOwnersPath(databaseId),
  paneFactory: (paneParams) => (
    <DatabaseDownstreamOwnersPane
      title={paneParams.title}
      databaseId={paneParams.databaseId}
      databaseVizportalId={paneParams.database.vizportalId}
      filteredTableId={paneParams.filteredTableId}
      rowCount={paneParams.count}
      databaseLuid={paneParams.database.luid}
    />
  )
};

function getDatabaseLineageConfiguration(
  metricsDataCatalogEnabled: boolean,
  publishedConnectionsLineageEnabled: boolean,
  lensLineageEnabled: boolean,
): ReadonlyArray<DatabaseLineageSection> {
  return [
    {
      sectionType: LineageSectionType.Group,
      key: 'upstream',
      items: DatabaseUpstreamItems,
    },
    {
      sectionType: LineageSectionType.Anchor,
      key: 'anchor',
      items: [DatabaseAnchor],
    },
    {
      sectionType: LineageSectionType.Group,
      key: 'downstream',
      items: memoizedGetDatabaseDownstreamItems(metricsDataCatalogEnabled, publishedConnectionsLineageEnabled, lensLineageEnabled),
    },
    {
      sectionType: LineageSectionType.Owners,
      key: 'owners',
      items: [DatabaseOwners],
    },
  ];
}
export const memoizedGetDatabaseLineageConfiguration = defaultMemoize(getDatabaseLineageConfiguration);
